import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Stock, WatchlistEntry } from '../types';
import { tradingConfig } from '../config/tradingConfig';

export class StockService {
  private static STOCKS_COLLECTION = 'stocks';
  private static WATCHLIST_COLLECTION = 'watchlist';

  static async addToWatchlist(stock: Omit<Stock, 'lastUpdated' | 'priceHistory' | 'priceWhenAdded'> & { priceWhenAdded?: number }): Promise<Stock> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const docRef = doc(db, this.STOCKS_COLLECTION, stock.symbol);
    
    // Remove undefined fields for Firestore
    const cleanStock = Object.entries(stock).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    const stockData = {
      ...cleanStock,
      priceWhenAdded: stock.priceWhenAdded || stock.currentPrice, // Use provided price or current price
      lastUpdated: serverTimestamp()
    };
    
    await setDoc(docRef, stockData);
    
    // Check if user's watchlist is currently shared
    const { SharedContentService } = await import('./sharedContentService');
    const isShared = await SharedContentService.isContentShared(user.uid, 'watchlist');
    
    // Also add to watchlist collection with user-specific key
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${stock.symbol}`);
    const watchlistEntry: any = {
      symbol: stock.symbol,
      userId: user.uid,
      addedAt: stock.addedToWatchlist,
      currentPrice: stock.currentPrice,
      isTracking: true,
      isShared: isShared, // Set based on current sharing status
      distanceFromPivot: stock.pivotPrice ? this.calculateDistanceFromPivot(stock.currentPrice, stock.pivotPrice) : null
    };
    
    // Add all optional fields if they're defined
    const optionalFields = [
      'pivotPrice', 'marketSurgeScore', 'targetPrice', 'companyName',
      'priceWhenAdded', 'epsRating', 'rsRating', 'weeklyChgToPivot',
      'weeklyBaseType', 'weeklyBaseStage', 'priceChgPercent', 'percentOffHigh',
      'volumeChgVs50Day', 'avgDailyVolume', 'marketCap', 'compRating', 'adRating', 'smrRating',
      'indGroupRank', 'industryName'
    ];
    
    optionalFields.forEach(field => {
      if ((stock as any)[field] !== undefined) {
        watchlistEntry[field] = (stock as any)[field];
      }
    });
    
    watchlistEntry.lastUpdated = serverTimestamp();
    
    await setDoc(watchlistRef, watchlistEntry);
    
    return { ...stock, lastUpdated: new Date() } as Stock;
  }


  static async updateStockPrice(symbol: string, currentPrice: number): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const stockRef = doc(db, this.STOCKS_COLLECTION, symbol);
    
    // Try both key formats for backward compatibility
    let watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    let watchlistSnap = await getDoc(watchlistRef);
    
    // If not found with new format, try old format
    if (!watchlistSnap.exists()) {
      watchlistRef = doc(db, this.WATCHLIST_COLLECTION, symbol);
      watchlistSnap = await getDoc(watchlistRef);
    }
    
    let distanceFromPivot = null;
    
    if (watchlistSnap.exists()) {
      const data = watchlistSnap.data();
      // Check if this entry belongs to the user or has no userId (legacy)
      if (data.userId && data.userId !== user.uid) {
        throw new Error('Access denied to this watchlist entry');
      }
      
      if (data.pivotPrice) {
        distanceFromPivot = this.calculateDistanceFromPivot(currentPrice, data.pivotPrice);
      }
      
      // Update watchlist collection
      await updateDoc(watchlistRef, {
        currentPrice,
        distanceFromPivot,
        lastUpdated: serverTimestamp(),
        userId: user.uid // Ensure userId is set
      });
    }
    
    // Update stock collection
    await updateDoc(stockRef, {
      currentPrice,
      lastUpdated: serverTimestamp()
    }).catch(() => {
      // Stock might not exist in the collection, that's ok
      console.log(`Stock ${symbol} not in stocks collection, skipping update`);
    });
  }

  static async updateStockPivot(symbol: string, pivotPrice: number): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const stockRef = doc(db, this.STOCKS_COLLECTION, symbol);
    
    // Try both key formats for backward compatibility
    let watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    let watchlistSnap = await getDoc(watchlistRef);
    
    // If not found with new format, try old format
    if (!watchlistSnap.exists()) {
      watchlistRef = doc(db, this.WATCHLIST_COLLECTION, symbol);
      watchlistSnap = await getDoc(watchlistRef);
    }
    
    if (watchlistSnap.exists()) {
      const data = watchlistSnap.data();
      // Check if this entry belongs to the user or has no userId (legacy)
      if (data.userId && data.userId !== user.uid) {
        throw new Error('Access denied to this watchlist entry');
      }
      
      const currentPrice = data.currentPrice || pivotPrice;
      const distanceFromPivot = this.calculateDistanceFromPivot(currentPrice, pivotPrice);
      
      // Update watchlist collection
      await updateDoc(watchlistRef, {
        pivotPrice,
        distanceFromPivot,
        lastUpdated: serverTimestamp(),
        userId: user.uid // Ensure userId is set
      });
    }
    
    // Update stock collection
    await updateDoc(stockRef, {
      pivotPrice,
      lastUpdated: serverTimestamp()
    }).catch(() => {
      // Stock might not exist in the collection, that's ok
      console.log(`Stock ${symbol} not in stocks collection, skipping update`);
    });
  }

  static async removeFromWatchlist(symbol: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    
    // Just mark as not tracking instead of deleting
    await updateDoc(watchlistRef, {
      isTracking: false,
      lastUpdated: serverTimestamp()
    });
  }

  static async getAllStocks(): Promise<Stock[]> {
    const querySnapshot = await getDocs(collection(db, this.STOCKS_COLLECTION));
    const stocks: Stock[] = [];
    
    querySnapshot.forEach((doc) => {
      stocks.push({
        ...doc.data(),
        symbol: doc.id,
        lastUpdated: doc.data().lastUpdated?.toDate()
      } as Stock);
    });
    
    return stocks;
  }

  static async getStock(symbol: string): Promise<Stock | null> {
    const docRef = doc(db, this.STOCKS_COLLECTION, symbol);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        symbol: docSnap.id,
        lastUpdated: docSnap.data().lastUpdated?.toDate()
      } as Stock;
    }
    
    return null;
  }

  static async stockExists(symbol: string): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) return false;
    
    try {
      const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
      const docSnap = await getDoc(watchlistRef);
      const exists = docSnap.exists();
      const isTracking = exists ? docSnap.data()?.isTracking : false;
      const result = exists && isTracking !== false;
      
      console.log(`Checking ${symbol}: docId=${user.uid}_${symbol}, exists=${exists}, isTracking=${isTracking}, result=${result}`);
      
      return result;
    } catch (error) {
      console.error(`Error checking if ${symbol} exists:`, error);
      // If we can't read, assume it doesn't exist
      return false;
    }
  }

  /**
   * Check if a stock exists in the user's watchlist (including inactive stocks)
   */
  static async stockExistsIncludingInactive(symbol: string): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) return false;
    
    try {
      const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
      const docSnap = await getDoc(watchlistRef);
      return docSnap.exists();
    } catch (error) {
      console.error(`Error checking if ${symbol} exists:`, error);
      return false;
    }
  }

  static async bulkUpdateWatchlist(updates: Array<{
    symbol: string;
    data: Partial<WatchlistEntry> & {
      currentPrice: number;
    }
  }>): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Update in parallel
    const promises = updates.map(async ({ symbol, data }) => {
      const stockRef = doc(db, this.STOCKS_COLLECTION, symbol);
      const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
      
      // Calculate distance from pivot if we have pivot price
      let distanceFromPivot = null;
      if (data.pivotPrice !== undefined) {
        distanceFromPivot = this.calculateDistanceFromPivot(data.currentPrice, data.pivotPrice);
      }
      
      // Update stock collection
      await updateDoc(stockRef, {
        ...data,
        lastUpdated: serverTimestamp()
      });
      
      // Update watchlist collection
      const watchlistSnap = await getDoc(watchlistRef);
      if (watchlistSnap.exists()) {
        const watchlistUpdate: any = {
          ...data,
          lastUpdated: serverTimestamp()
        };
        
        if (distanceFromPivot !== null) {
          watchlistUpdate.distanceFromPivot = distanceFromPivot;
        }
        
        await updateDoc(watchlistRef, watchlistUpdate);
      }
    });
    
    await Promise.all(promises);
  }

  private static calculateDistanceFromPivot(currentPrice: number, pivotPrice: number): number {
    return ((currentPrice - pivotPrice) / pivotPrice) * 100;
  }

  static isNearPivotPoint(stock: WatchlistEntry): boolean {
    if (!stock.distanceFromPivot) return false;
    
    const maxDistance = tradingConfig.maxDistanceFromPivot;
    const minDistance = tradingConfig.minDistanceFromPivot || -5;
    
    return stock.distanceFromPivot >= minDistance && 
           stock.distanceFromPivot <= maxDistance;
  }

  static async getWatchlistWithDetails(): Promise<WatchlistEntry[]> {
    return this.getWatchlist();
  }

  static calculatePivotPrice(currentPrice: number, weeklyChgToPivot: number): number {
    // weeklyChgToPivot represents how far current price is from pivot:
    // - Negative % means current price is ABOVE pivot (past it)
    // - Positive % means current price is BELOW pivot (approaching it)
    // 
    // Example: -11.63% means current price is 11.63% above pivot
    // So if current = 383.82 and change = -11.63%, then:
    // pivot = 383.82 / (1 + 0.1163) = 383.82 / 1.1163 = 343.88
    
    // Convert the percentage to its absolute value and determine direction
    const changeRatio = weeklyChgToPivot / 100;
    
    if (weeklyChgToPivot < 0) {
      // Current price is above pivot, so pivot is lower
      return currentPrice / (1 + Math.abs(changeRatio));
    } else {
      // Current price is below pivot, so pivot is higher
      return currentPrice / (1 - changeRatio);
    }
  }

  static calculateTargetPrice(pivotPrice: number): number {
    return pivotPrice * (1 + tradingConfig.targetPricePercentage / 100);
  }

  static async updateWatchlistStock(stockData: Partial<Stock> & { symbol: string }): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const { symbol, ...updateData } = stockData;
    const stockRef = doc(db, this.STOCKS_COLLECTION, symbol);
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    
    // Remove fields that should NOT be updated (preserve historical data)
    const fieldsToPreserve = ['addedToWatchlist', 'priceWhenAdded', 'addedAt'];
    const cleanedUpdateData = { ...updateData };
    fieldsToPreserve.forEach(field => {
      delete (cleanedUpdateData as any)[field];
    });
    
    // Update stock collection (but not historical fields)
    await updateDoc(stockRef, {
      ...cleanedUpdateData,
      lastUpdated: serverTimestamp()
    }).catch(() => {
      // Stock might not exist, that's ok
    });
    
    // Update watchlist collection (but not historical fields)
    const watchlistUpdate: any = {
      ...cleanedUpdateData,
      lastUpdated: serverTimestamp()
    };
    
    // Recalculate distance from pivot if needed
    if (updateData.pivotPrice && updateData.currentPrice) {
      watchlistUpdate.distanceFromPivot = this.calculateDistanceFromPivot(
        updateData.currentPrice,
        updateData.pivotPrice
      );
    }
    
    await updateDoc(watchlistRef, watchlistUpdate);
  }

  static async updateStockField(symbol: string, field: string, value: any): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const stockRef = doc(db, this.STOCKS_COLLECTION, symbol);
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    
    // Update both collections
    const updateData = {
      [field]: value,
      lastUpdated: serverTimestamp()
    };
    
    await updateDoc(stockRef, updateData);
    await updateDoc(watchlistRef, updateData);
  }

  static async getStocksNearPivot(thresholdPercent: number = 5): Promise<WatchlistEntry[]> {
    const watchlist = await this.getWatchlist();
    return watchlist.filter(stock => {
      if (!stock.distanceFromPivot || !stock.pivotPrice) return false;
      return Math.abs(stock.distanceFromPivot) <= thresholdPercent;
    });
  }

  /**
   * Set a stock as inactive (soft delete)
   */
  static async setStockInactive(symbol: string, reason?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    
    await updateDoc(watchlistRef, {
      isActive: false,
      inactivatedAt: serverTimestamp(),
      inactivatedReason: reason || 'User removed from watchlist',
      lastUpdated: serverTimestamp()
    });
  }
  
  /**
   * Reactivate an inactive stock
   */
  static async reactivateStock(symbol: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    
    await updateDoc(watchlistRef, {
      isActive: true,
      inactivatedAt: null,
      inactivatedReason: null,
      lastUpdated: serverTimestamp()
    });
  }
  
  /**
   * Permanently delete an inactive stock (if allowed)
   */
  static async deleteInactiveStock(symbol: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Check if stock has position history
    const { PositionService } = await import('./positionService');
    const hasPositionHistory = await PositionService.hasPositionHistory(symbol, user.uid);
    
    if (hasPositionHistory) {
      throw new Error('Cannot delete stock with position history. Stock data preserved for historical tracking.');
    }
    
    // Delete from watchlist collection
    const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
    await updateDoc(watchlistRef, {
      isTracking: false,
      deletedAt: serverTimestamp()
    });
    
    // TODO: Also delete watchlist snapshots for this user and symbol
  }
  
  /**
   * Get watchlist with option to include/exclude inactive stocks
   */
  static async getWatchlist(includeInactive: boolean = false): Promise<WatchlistEntry[]> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const watchlistQuery = query(
      collection(db, this.WATCHLIST_COLLECTION),
      where('userId', '==', user.uid)
    );
    
    const snapshot = await getDocs(watchlistQuery);
    
    if (snapshot.empty) {
      console.log('No watchlist entries found for user:', user.uid);
      return [];
    }
    
    const entries: WatchlistEntry[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Skip if not tracking (deleted)
      if (data.isTracking === false || data.deletedAt) {
        return;
      }
      
      // Skip inactive stocks unless requested
      if (!includeInactive && data.isActive === false) {
        return;
      }
      
      entries.push({
        symbol: data.symbol,
        userId: data.userId,
        addedAt: data.addedAt?.toDate() || new Date(),
        pivotPrice: data.pivotPrice,
        currentPrice: data.currentPrice || 0,
        distanceFromPivot: data.distanceFromPivot ?? null,
        marketSurgeScore: data.marketSurgeScore,
        notes: data.notes,
        isTracking: data.isTracking ?? true,
        targetPrice: data.targetPrice,
        companyName: data.companyName,
        priceWhenAdded: data.priceWhenAdded,
        lastUpdated: data.lastUpdated?.toDate(),
        epsRating: data.epsRating,
        rsRating: data.rsRating,
        weeklyChgToPivot: data.weeklyChgToPivot,
        weeklyBaseType: data.weeklyBaseType,
        weeklyBaseStage: data.weeklyBaseStage,
        priceChgPercent: data.priceChgPercent,
        percentOffHigh: data.percentOffHigh,
        volume: data.volume,
        volumeChgVs50Day: data.volumeChgVs50Day,
        marketCap: data.marketCap,
        compRating: data.compRating,
        adRating: data.adRating,
        smrRating: data.smrRating,
        indGroupRank: data.indGroupRank,
        industryName: data.industryName,
        avgDailyVolume: data.avgDailyVolume,
        volumeSpikeRatio: data.volumeSpikeRatio,
        tradingDayProgress: data.tradingDayProgress,
        isActive: data.isActive !== false, // Default to true if not specified
        inactivatedAt: data.inactivatedAt?.toDate(),
        inactivatedReason: data.inactivatedReason,
      });
    });
    
    return entries;
  }
  
  /**
   * Get a specific user's watchlist (for shared watchlists)
   */
  /**
   * Get unique field values from all stocks for dropdown options
   */
  static async getUniqueFieldValues(): Promise<{
    baseTypes: string[];
    baseStages: string[];
    adRatings: string[];
    smrRatings: string[];
    industries: string[];
  }> {
    try {
      const functions = getFunctions();
      const getFieldValues = httpsCallable(functions, 'getStockFieldValues');
      const result = await getFieldValues();
      
      if (result.data && (result.data as any).success) {
        return (result.data as any).data;
      } else {
        throw new Error('Failed to get field values');
      }
    } catch (error) {
      console.error('Error getting unique field values:', error);
      // Return empty arrays as fallback
      return {
        baseTypes: [],
        baseStages: [],
        adRatings: [],
        smrRatings: [],
        industries: []
      };
    }
  }
  
  static async getUserWatchlist(userId: string, includeInactive: boolean = false): Promise<WatchlistEntry[]> {
    console.log('[getUserWatchlist] Called with userId:', userId);
    
    // First check if this is the current user
    const user = auth.currentUser;
    console.log('[getUserWatchlist] Current user:', user?.uid);
    const isCurrentUser = user && user.uid === userId;
    console.log('[getUserWatchlist] Is current user?', isCurrentUser);
    
    // Build the query constraints
    const constraints: any[] = [
      where('userId', '==', userId)
    ];
    
    // If not current user, only get shared entries
    if (!isCurrentUser) {
      console.log('[getUserWatchlist] Adding isShared=true constraint');
      constraints.push(where('isShared', '==', true));
    } else {
      console.log('[getUserWatchlist] Current user - no isShared constraint');
    }
    
    console.log('[getUserWatchlist] Building query with constraints:', constraints.length);
    
    const watchlistQuery = query(
      collection(db, this.WATCHLIST_COLLECTION),
      ...constraints
    );
    
    let snapshot;
    try {
      console.log('[getUserWatchlist] Executing query...');
      snapshot = await getDocs(watchlistQuery);
      console.log('[getUserWatchlist] Query successful, returned', snapshot.size, 'documents');
    } catch (error: any) {
      console.error('[getUserWatchlist] Query failed:', error.code, error.message);
      throw error;
    }
    
    if (snapshot.empty) {
      console.log('No watchlist entries found for user:', userId);
      return [];
    }
    
    const entries: WatchlistEntry[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Skip if not tracking (deleted)
      if (data.isTracking === false || data.deletedAt) {
        return;
      }
      
      // Skip inactive stocks unless requested
      if (!includeInactive && data.isActive === false) {
        return;
      }
      
      entries.push({
        symbol: data.symbol,
        userId: data.userId,
        addedAt: data.addedAt?.toDate() || new Date(),
        pivotPrice: data.pivotPrice,
        currentPrice: data.currentPrice || 0,
        distanceFromPivot: data.distanceFromPivot ?? null,
        marketSurgeScore: data.marketSurgeScore,
        notes: data.notes,
        isTracking: data.isTracking ?? true,
        targetPrice: data.targetPrice,
        companyName: data.companyName,
        priceWhenAdded: data.priceWhenAdded,
        lastUpdated: data.lastUpdated?.toDate(),
        epsRating: data.epsRating,
        rsRating: data.rsRating,
        weeklyChgToPivot: data.weeklyChgToPivot,
        weeklyBaseType: data.weeklyBaseType,
        weeklyBaseStage: data.weeklyBaseStage,
        priceChgPercent: data.priceChgPercent,
        percentOffHigh: data.percentOffHigh,
        volume: data.volume,
        volumeChgVs50Day: data.volumeChgVs50Day,
        marketCap: data.marketCap,
        compRating: data.compRating,
        adRating: data.adRating,
        smrRating: data.smrRating,
        indGroupRank: data.indGroupRank,
        industryName: data.industryName,
        avgDailyVolume: data.avgDailyVolume,
        volumeSpikeRatio: data.volumeSpikeRatio,
        tradingDayProgress: data.tradingDayProgress,
        isActive: data.isActive !== false, // Default to true if not specified
        inactivatedAt: data.inactivatedAt?.toDate(),
        inactivatedReason: data.inactivatedReason,
      });
    });
    
    return entries;
  }
  
  /**
   * Import watchlist from another user
   */
  static async importWatchlistFromUser(sourceUserId: string, excludeInactive: boolean = true): Promise<{ imported: string[], skipped: string[], updated: string[] }> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Get source user's watchlist
    const sourceQuery = query(
      collection(db, this.WATCHLIST_COLLECTION),
      where('userId', '==', sourceUserId)
    );
    
    const sourceSnapshot = await getDocs(sourceQuery);
    
    if (sourceSnapshot.empty) {
      return { imported: [], skipped: [], updated: [] };
    }
    
    // Get current user's watchlist to check existing stocks
    const currentWatchlist = await this.getWatchlist(true); // Include inactive to check
    const currentSymbols = new Map(
      currentWatchlist.map(entry => [entry.symbol, entry])
    );
    
    const result = { imported: [] as string[], skipped: [] as string[], updated: [] as string[] };
    
    for (const sourceDoc of sourceSnapshot.docs) {
      const sourceData = sourceDoc.data();
      
      // Skip if not tracking or deleted in source
      if (sourceData.isTracking === false || sourceData.deletedAt) {
        continue;
      }
      
      // Skip inactive stocks if requested
      if (excludeInactive && sourceData.isActive === false) {
        continue;
      }
      
      const symbol = sourceData.symbol;
      const existingEntry = currentSymbols.get(symbol);
      
      // Skip if user previously inactivated this stock
      if (existingEntry && existingEntry.isActive === false) {
        result.skipped.push(symbol);
        continue;
      }
      
      // Check if current user's watchlist is shared
      const { SharedContentService } = await import('./sharedContentService');
      const currentUserWatchlistShared = await SharedContentService.isContentShared(user.uid, 'watchlist');
      
      // Prepare watchlist entry with only defined values
      const watchlistEntry: any = {
        symbol,
        userId: user.uid,
        addedAt: serverTimestamp(),
        currentPrice: sourceData.currentPrice || 0,
        isTracking: true,
        isActive: true,
        isShared: currentUserWatchlistShared, // Set based on current user's sharing status
        priceWhenAdded: sourceData.currentPrice || 0,
        lastUpdated: sourceData.lastUpdated || serverTimestamp(),
      };
      
      // Only add fields that are defined
      const optionalFields = [
        'pivotPrice', 'distanceFromPivot', 'companyName', 'targetPrice',
        'marketSurgeScore', 'epsRating', 'rsRating', 'weeklyChgToPivot', 'weeklyBaseType', 
        'weeklyBaseStage', 'priceChgPercent', 'percentOffHigh', 'volume',
        'volumeChgVs50Day', 'marketCap', 'compRating', 'adRating', 'smrRating',
        'indGroupRank', 'industryName', 'avgDailyVolume', 'volumeSpikeRatio',
        'tradingDayProgress'
      ];
      
      optionalFields.forEach(field => {
        if (sourceData[field] !== undefined && sourceData[field] !== null) {
          watchlistEntry[field] = sourceData[field];
        }
      });
      
      const watchlistRef = doc(db, this.WATCHLIST_COLLECTION, `${user.uid}_${symbol}`);
      
      if (existingEntry) {
        // Update existing entry
        await updateDoc(watchlistRef, {
          ...watchlistEntry,
          addedAt: existingEntry.addedAt // Keep original add date
        });
        result.updated.push(symbol);
      } else {
        // Import new entry
        await setDoc(watchlistRef, watchlistEntry);
        result.imported.push(symbol);
      }
    }
    
    // Record import in user profile
    const { UserService } = await import('./userService');
    await UserService.recordWatchlistImport(
      user.uid, 
      sourceUserId, 
      [...result.imported, ...result.updated]
    );
    
    return result;
  }
}